import { mqttLogger } from '@hooks/mqtt/utils';
import { Getter, Setter } from 'jotai';
import { MqttClient } from 'mqtt';
import {
  mqttClientAtom,
  mqttConnectionAtom,
} from '../connection/mqttConnectionAtom';
import { MqttConnectionActionType } from '../connection/types';
import { SubscriptionConfig } from '../types';
import {
  handleSubscriptionError,
  logSubscriptionEvent,
  SubscriptionEvent,
} from './atomUtils';

export async function ensureClientConnected(
  get: Getter,
  set: Setter,
  topic: string,
): Promise<MqttClient | null> {
  let client: MqttClient | null = get(mqttClientAtom);

  if (client && client.connected) {
    return client;
  }

  try {
    client = (await set(mqttConnectionAtom, {
      type: MqttConnectionActionType.ENSURE,
    })) as MqttClient | null;

    if (!client) {
      logSubscriptionEvent(SubscriptionEvent.Error, topic, {
        message: `Cannot subscribe to ${topic}: MQTT client initialization failed`,
      });
      return null;
    }
  } catch (error) {
    logSubscriptionEvent(SubscriptionEvent.Error, topic, {
      message: `Cannot subscribe to ${topic}: ${error instanceof Error ? error.message : 'MQTT client initialization failed'}`,
    });
    return null;
  }

  if (!client.connected) {
    logSubscriptionEvent(SubscriptionEvent.Error, topic, {
      message: `Cannot subscribe to ${topic}: MQTT client not connected to broker`,
    });
    return null;
  }

  return client;
}

export async function performMqttSubscription(
  client: MqttClient,
  topic: string,
  config?: SubscriptionConfig,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    client.subscribe(topic, { qos: config?.qos ?? 0 }, (err: Error | null) => {
      if (err) {
        mqttLogger.error(`MQTT subscribe error for ${topic}:`, err);
        reject(err);
      } else {
        resolve();
      }
    });
  });
}

export function handleSubscriptionFailure(topic: string, error: unknown): void {
  handleSubscriptionError(topic, error, 'Failed to subscribe');
}

export function cleanupAtomFamilies(
  topic: string,
  ...atomFamilies: Array<{ remove: (param: string) => void }>
): void {
  atomFamilies.forEach((atomFamily) => {
    atomFamily.remove(topic);
  });
}

export async function unsubscribeFromMqttTopic(
  client: MqttClient,
  topic: string,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    client.unsubscribe(topic, (err) => {
      if (err) {
        reject(err);
      } else {
        resolve();
      }
    });
  });
}

export async function handleLastSubscriberUnsubscribe(
  client: MqttClient | null,
  topic: string,
): Promise<void> {
  if (!client) {
    return;
  }

  try {
    await unsubscribeFromMqttTopic(client, topic);
    logSubscriptionEvent(SubscriptionEvent.Unsubscribe, topic);
  } catch (error) {
    mqttLogger.error(`Failed to unsubscribe from topic ${topic}:`, error);
  }
}
